// ManageVolunteers.js
import React, { useState, useEffect } from 'react';
import { 
  getAllVolunteers, 
  updateVolunteerStatus, 
  deleteVolunteer 
} from '../../api/volunteers';
import { getAllEvents } from '../../api/events';
import { getAllUsers } from '../../api/users';
import Button from '../../components/ui/Button';
import './ManageEvents.css';

const ManageVolunteers = () => {
  const [volunteers, setVolunteers] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterEvent, setFilterEvent] = useState('all');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [volunteersData, eventsData, usersData] = await Promise.all([
        getAllVolunteers(),
        getAllEvents(),
        getAllUsers()
      ]);
      
      setVolunteers(volunteersData);
      setEvents(eventsData);
      setUsers(usersData);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load volunteers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (volunteerId, newStatus) => {
    try {
      setSubmitting(true);
      await updateVolunteerStatus(volunteerId, newStatus);
      
      // Update local state
      setVolunteers(volunteers.map(v => 
        v.id === volunteerId ? { ...v, status: newStatus } : v
      ));
      
      setSuccess(`Volunteer status updated to ${newStatus}!`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating volunteer status:', err);
      setError('Failed to update status. Please try again.');
      setTimeout(() => setError(null), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (volunteerId) => {
    if (!window.confirm('Are you sure you want to delete this volunteer registration?')) {
      return;
    }

    try {
      setSubmitting(true);
      await deleteVolunteer(volunteerId);
      setVolunteers(volunteers.filter(v => v.id !== volunteerId));
      setSuccess('Volunteer deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting volunteer:', err);
      setError('Failed to delete volunteer. Please try again.');
      setTimeout(() => setError(null), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const getEventTitle = (eventId) => {
    const event = events.find(e => e.id === eventId);
    return event ? event.title : 'Unknown Event';
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Unknown User';
  };

  const getUserEmail = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.email : 'N/A';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'status-pending', label: 'Pending' },
      approved: { class: 'status-approved', label: 'Approved' },
      rejected: { class: 'status-rejected', label: 'Rejected' },
      completed: { class: 'status-completed', label: 'Completed' }
    };
    return badges[status] || badges.pending;
  };

  // Filter volunteers
  const filteredVolunteers = volunteers.filter(volunteer => {
    const matchesStatus = filterStatus === 'all' || volunteer.status === filterStatus;
    const matchesEvent = filterEvent === 'all' || volunteer.eventId === filterEvent;
    return matchesStatus && matchesEvent;
  });

  // Calculate statistics
  const stats = {
    total: volunteers.length,
    pending: volunteers.filter(v => v.status === 'pending').length,
    approved: volunteers.filter(v => v.status === 'approved').length,
    completed: volunteers.filter(v => v.status === 'completed').length
  };

  if (loading) {
    return (
      <div className="manage-events">
        <div className="loading-spinner">Loading volunteers...</div>
      </div>
    );
  }

  return (
    <div className="manage-events manage-volunteers">
      <div className="manage-events-header">
        <h1>Manage Volunteers</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Statistics Cards */}
      <div className="volunteer-stats">
        <div className="stat-card-small">
          <div className="stat-label">Total</div>
          <div className="stat-value">{stats.total}</div>
        </div>
        <div className="stat-card-small stat-pending">
          <div className="stat-label">Pending</div>
          <div className="stat-value">{stats.pending}</div>
        </div>
        <div className="stat-card-small stat-approved">
          <div className="stat-label">Approved</div>
          <div className="stat-value">{stats.approved}</div>
        </div>
        <div className="stat-card-small stat-completed">
          <div className="stat-label">Completed</div>
          <div className="stat-value">{stats.completed}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="event-form-container" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ marginRight: '0.5rem', fontWeight: 'bold' }}>Filter by Status:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem',
                minWidth: '150px'
              }}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div>
            <label style={{ marginRight: '0.5rem', fontWeight: 'bold' }}>Filter by Event:</label>
            <select
              value={filterEvent}
              onChange={(e) => setFilterEvent(e.target.value)}
              style={{
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem',
                minWidth: '200px'
              }}
            >
              <option value="all">All Events</option>
              {events.map(event => (
                <option key={event.id} value={event.id}>{event.title}</option>
              ))}
            </select>
          </div>

          <div style={{ color: '#666', fontSize: '0.9rem', marginLeft: 'auto' }}>
            Showing {filteredVolunteers.length} of {volunteers.length} volunteers
          </div>
        </div>
      </div>

      {/* Volunteers Table */}
      <div className="events-table-container">
        <table className="events-table">
          <thead>
            <tr>
              <th>Volunteer Name</th>
              <th>Email</th>
              <th>Event</th>
              <th>Status</th>
              <th>Registered Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredVolunteers.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  {volunteers.length === 0 
                    ? 'No volunteers found.' 
                    : 'No volunteers match the selected filters.'}
                </td>
              </tr>
            ) : (
              filteredVolunteers.map((volunteer) => {
                const statusBadge = getStatusBadge(volunteer.status);
                return (
                  <tr key={volunteer.id}>
                    <td>
                      <strong>{getUserName(volunteer.userId)}</strong>
                    </td>
                    <td>{getUserEmail(volunteer.userId)}</td>
                    <td>{getEventTitle(volunteer.eventId)}</td>
                    <td>
                      <span className={`status-badge ${statusBadge.class}`}>
                        {statusBadge.label}
                      </span>
                    </td>
                    <td>{formatDate(volunteer.createdAt)}</td>
                    <td>
                      <div className="action-buttons" style={{ flexDirection: 'column', gap: '0.25rem', alignItems: 'stretch' }}>
                        {volunteer.status === 'pending' && (
                          <>
                            <Button 
                              onClick={() => handleStatusChange(volunteer.id, 'approved')} 
                              variant="primary"
                              size="small"
                              disabled={submitting}
                            >
                              Approve
                            </Button>
                            <Button 
                              onClick={() => handleStatusChange(volunteer.id, 'rejected')} 
                              variant="secondary"
                              size="small"
                              disabled={submitting}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        {volunteer.status === 'approved' && (
                          <Button 
                            onClick={() => handleStatusChange(volunteer.id, 'completed')} 
                            variant="primary"
                            size="small"
                            disabled={submitting}
                          >
                            Mark Complete
                          </Button>
                        )}
                        <Button 
                          onClick={() => handleDelete(volunteer.id)} 
                          variant="danger"
                          size="small"
                          disabled={submitting}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManageVolunteers;
